import * as React from 'react';
import { type VariantProps } from 'class-variance-authority';
import { useSession } from '@/hooks/index';
import { ModelImage } from '@acme/db';
import { ListItem } from './ListItem';
import { Samples } from './Samples';
import { SamplesWithCTA } from './SamplesWithCTA';
import { photoPackVariants } from './utils';
import { Hero } from './Hero';
import { CloudflareTransform } from '@lib/transformCloudflareImageUrl';
import Link from 'next/link';
import { cn } from '@lib/utils';
import { PhotoPackSamplesSheet } from '@components/Sheets/PhotoPackSamples';
import { PhotoPackSamplePhoto } from '@components/Theme/PhotoPackSamplePhoto';
import { ImageIcon, Loader2 } from 'lucide-react';
import { Badge } from '@components/ui/badge';

// Define the props interface
export interface PhotoPackV3Props extends VariantProps<typeof photoPackVariants> {
  /**
   * Name of the photo pack
   */
  name: string;

  /**
   * Accent color for styling
   */
  accentColor?: string;

  /**
   * Additional class names
   */
  className?: string;

  /**
   * Whether to enable the sample gallery
   */
  enableSampleGallery?: boolean;

  /**
   * ID of the photo pack
   */
  photoPackId: string;

  /**
   * Number of photos in the pack
   */
  photoCount?: number;

  /**
   * Whether to show the photo count
   */
  showPhotoCount?: boolean;

  /**
   * Whether the photo pack is checked/selected
   */
  checked?: boolean;

  /**
   * Number of times the pack has been used
   */
  usageCount?: number;

  /**
   * Sample images to display
   */
  sampleImages?: (Pick<ModelImage, 'id' | 'imageUrl'> & { selfieImageUrl?: string | null })[];

  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;

  /**
   * tag of the photo pack
   */
  tag?: string;

  /**
   * Whether to show the usage count
   */
  showUsageCount?: boolean;

  /**
   * Callback when the photo pack is clicked
   */
  onCheckedChange?: (checked: boolean) => void;

  /**
   * Relationship type for the photo pack
   */
  relationship?: 'couple' | 'friends';

  /**
   * Whether to use mesh gradient for background
   */
  useMeshGradient?: boolean;

  /**
   * Single color to use for gradient background
   * When provided, this will be used instead of extracting colors from the image
   */
  singleColor?: string;

  /**
   * Base URL for the photo pack
   */
  hrefBase?: string;

  /**
   * Whether to disable the links in the variant
   */
  disableLink?: boolean;

  /**
   * Whether to show the selfie in the samples
   */
  showSelfies?: boolean;

  /**
   * Cloudflare variant to use for the sample images
   */
  cloudflareVariant?: CloudflareTransform['variant'];

  /**
   * Whether to show skeleton state
   */
  skeleton?: boolean;

  /**
   * Whether to hide the Get/Edit button
   */
  hideGetButton?: boolean;

  /**
   * Slug of the photo pack
   */
  slug?: string;

  /**
   * Primary dark color of the photo pack
   */
  primaryDarkColor?: string;

  /**
   * Secondary dark color of the photo pack
   */
  secondaryDarkColor?: string;
}

export function PhotoPackTag({
  photoPack,
  disableLink,
  className
}: {
  photoPack: {
    tag: {
      name: string;
      url: string;
    };
  };
  disableLink: boolean;
  className?: string;
}) {
  return disableLink ? (
    <span className={cn(className)}>{photoPack?.tag.name}</span>
  ) : (
    <Link href={photoPack.tag.url} className={cn(className, 'hover:underline')}>
      {photoPack?.tag.name}
    </Link>
  );
}

export function PhotoPackName({
  photoPack,
  disableLink,
  className
}: {
  photoPack: { name: string; url: string };
  disableLink: boolean;
  className?: string;
}) {
  return disableLink ? (
    <span className={cn(className)}>{photoPack?.name}</span>
  ) : (
    <Link href={photoPack.url} className={cn(className, 'hover:underline')}>
      {photoPack?.name}
    </Link>
  );
}

/**
 * PhotoPackGrid - A component for displaying a grid of photo pack sample images
 */
export function PhotoPackGrid({
  limitSamples = 2,
  displayImages = [],
  disableLink = false,
  setIsSheetOpen,
  photoPack,
  className,
  showSelfies = false,
  cloudflareVariant = 'public'
}: // forceTallAspectRatio = false
{
  limitSamples?: number;
  displayImages: (Pick<ModelImage, 'id' | 'imageUrl'> & { selfieImageUrl?: string | null })[];
  disableLink?: boolean;
  setIsSheetOpen?: (open: boolean) => void;
  photoPack?: {
    url: string;
  };
  className?: string;
  showSelfies?: boolean;
  cloudflareVariant?: CloudflareTransform['variant'];
  forceTallAspectRatio?: boolean;
}) {
  // Ensure the correct number of skeletons are rendered
  const imagesToRender = displayImages.length > 0 ? displayImages : Array.from({ length: limitSamples }, () => null);
  const showSkeletons = displayImages.length === 0;

  return (
    <div className={cn(className, 'cursor-pointer', 'relative')}>
      {imagesToRender.map((image, index) =>
        image ? (
          disableLink ? (
            <div
              key={image.id}
              className="relative w-full h-auto aspect-[4/3] overflow-hidden"
              onClick={() => setIsSheetOpen?.(true)}
            >
              <PhotoPackSamplePhoto
                imageUrl={image.imageUrl}
                id={image.id}
                showSelfie={showSelfies}
                cloudflareVariant={cloudflareVariant}
              />
            </div>
          ) : (
            <Link href={photoPack?.url ?? '#'} className="relative w-full h-full block" key={image.id}>
              <div className="relative w-full h-auto aspect-[4/3] overflow-hidden">
                <PhotoPackSamplePhoto
                  imageUrl={image.imageUrl}
                  id={image.id}
                  showSelfie={showSelfies}
                  cloudflareVariant={cloudflareVariant}
                />
              </div>
            </Link>
          )
        ) : (
          <div key={index} className="relative w-full h-auto aspect-[4/3] overflow-hidden bg-foreground/10">
            <ImageIcon className="w-4 h-4 opacity-50 text-white/40 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
        )
      )}

      {/* Single text overlay for all skeletons */}
      {showSkeletons && (
        <Badge
          variant="outline-warning"
          className="dark absolute inset-0 flex items-center justify-center pointer-events-none z-10 h-fit w-fit top-2 left-2"
        >
          <Loader2 className="w-3 h-3 animate-spin mr-1" /> Generating samples
        </Badge>
      )}
    </div>
  );
}

/**
 * PhotoPackV3 - A component for displaying photo packs with different variants
 */
export function PhotoPackV3({
  variant = 'samples',
  name,
  className,
  enableSampleGallery = false,
  photoPackId,
  photoCount,
  showPhotoCount = false,
  checked = false,
  usageCount,
  sampleImages = [],
  style,
  tag,
  showUsageCount = true,
  onCheckedChange,
  relationship,
  useMeshGradient = false,
  singleColor,
  disableLink = false,
  showSelfies = false,
  cloudflareVariant = 'public',
  skeleton = false,
  hideGetButton = false,
  slug,
  primaryDarkColor,
  secondaryDarkColor
}: PhotoPackV3Props) {
  const { isLoggedIn } = useSession();
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);

  const commonProps = {
    name,
    className,
    enableSampleGallery,
    photoPackId,
    photoCount,
    showPhotoCount,
    checked,
    usageCount,
    style,
    tag,
    showUsageCount,
    onCustomize: onCheckedChange,
    relationship,
    useMeshGradient,
    singleColor,
    isSheetOpen,
    setIsSheetOpen,
    disableLink,
    showSelfies,
    cloudflareVariant,
    hideGetButton,
    photoPack: {
      name,
      sampleImages,
      tag: {
        name: tag ?? '',
        url: `/headshot-types/${tag}`
      },
      id: photoPackId,
      url: slug ? `/packs/browse/${slug}` : `/packs/browse/${photoPackId}`,
      primaryDarkColor,
      secondaryDarkColor
    },
    skeleton
  };

  // Render the appropriate variant
  switch (variant) {
    case 'listItem':
      return (
        <>
          <ListItem {...commonProps} isLoggedIn={isLoggedIn} />
          <PhotoPackSamplesSheet
            photoPackId={photoPackId}
            isSheetOpen={isSheetOpen}
            onSheetOpenChange={setIsSheetOpen}
            onCustomize={open => {
              // Close the sheet so we can open the photo pack details
              setIsSheetOpen(false);
              onCheckedChange?.(open);
            }}
          />
        </>
      );
    case 'samples':
      return (
        <>
          <Samples {...commonProps} />
          <PhotoPackSamplesSheet
            photoPackId={photoPackId}
            isSheetOpen={isSheetOpen}
            onSheetOpenChange={setIsSheetOpen}
            onCustomize={open => {
              // Close the sheet so we can open the photo pack details
              setIsSheetOpen(false);
              onCheckedChange?.(open);
            }}
          />
        </>
      );
    case 'samples-w-cta':
      return (
        <>
          <SamplesWithCTA {...commonProps} />
          <PhotoPackSamplesSheet
            photoPackId={photoPackId}
            isSheetOpen={isSheetOpen}
            onSheetOpenChange={setIsSheetOpen}
            onCustomize={open => {
              // Close the sheet so we can open the photo pack details
              setIsSheetOpen(false);
              onCheckedChange?.(open);
            }}
          />
        </>
      );
    case 'hero':
      return (
        <>
          <Hero {...commonProps} />
          <PhotoPackSamplesSheet
            photoPackId={photoPackId}
            isSheetOpen={isSheetOpen}
            onSheetOpenChange={setIsSheetOpen}
            onCustomize={open => {
              // Close the sheet so we can open the photo pack details
              setIsSheetOpen(false);
              onCheckedChange?.(open);
            }}
          />
        </>
      );
    default:
      return null;
  }
}

export default PhotoPackV3;
